import { CollapsedTaskGlow } from './CollapsedTaskGlow';
import { GeneratedImagePreview } from './GeneratedImagePreview';
import { playbookPhaseLabel } from './playbook-phase-label';
import { TaskProgressBar } from './TaskProgressBar';
import { useState } from 'react';
import type { PlaybookProgressState, ChatImageSlot } from '@/pages/canvas/canvas-types';
import { useActiveProjectImages } from '@/pages/canvas/store-selectors';

interface PlaybookTaskCardProps {
  progress: PlaybookProgressState;
  messageId: string;
  isActive: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** This message's own image slots (resolved URLs from SSE per-message tracking).
   *  Used to scope the thumbnail strip to THIS playbook's ads instead of
   *  the project-wide last-8 (which made every card show the same images). */
  messageImageSlots?: ChatImageSlot[];
  /** A separate message gallery owns previews when supplied. */
  showThumbnails?: boolean;
}

export function PlaybookTaskCard({ progress, messageId, messageImageSlots, showThumbnails = true, isActive, expanded: controlledExpanded, onExpandedChange }: PlaybookTaskCardProps) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = controlledExpanded ?? localExpanded;
  const setExpanded = onExpandedChange ?? setLocalExpanded;
  const images = useActiveProjectImages();

  const isComplete = progress.isDone;
  const collapsedRunning = !expanded && isActive && !isComplete && !Object.values(progress.phaseStatuses).includes('failed');
  const completedCount = progress.phases.filter((p) => progress.phaseStatuses[p.id] === 'completed').length;
  const totalCount = progress.phases.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const adGenerationDone = progress.phaseStatuses['ad_generation'] === 'completed'
    || progress.phaseStatuses['hero_generation'] === 'completed';

  // PER-MESSAGE thumbnails. Previously this used the project-wide last-8
  // images, which meant every PlaybookTaskCard in chat scrollback showed
  // the SAME ads (the latest batch). Now we look up images by the message's
  // own resolved imageSlot URLs — each card shows the ads that THIS run
  // produced.
  //
  // Fallback to the legacy project-last-8 only when messageImageSlots is
  // empty (older messages saved before per-message slots existed).
  const slotUrls = (messageImageSlots ?? [])
    .map((s) => s?.resolved)
    .filter((u): u is string => typeof u === 'string' && u.length > 0);

  const thumbnails = showThumbnails && adGenerationDone
    ? slotUrls.length > 0
      ? (() => {
          const urlSet = new Set(slotUrls);
          // Preserve store order, filter to this message's URLs.
          const matched = images.filter((img) => urlSet.has(img.src));
          // If image records haven't landed yet (race), fall back to the
          // raw URLs by synthesizing minimal img records.
          if (matched.length === slotUrls.length) return matched;
          return slotUrls.map((url, i) => matched.find((m) => m.src === url) ?? {
            id: `slot-${messageId}-${i}`,
            src: url,
            adStyle: undefined,
            adAngle: undefined,
          } as typeof images[number]);
        })()
      : images.filter((img) => img.adStyle || img.adAngle).slice(-8)
    : [];

  return (
    <div
      data-testid={`playbook-task-card-${messageId}`}
      style={{
        background: '#eceef3',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        borderRadius: 24,
        padding: '20px 24px',
        marginBottom: 12,
        marginTop: 12,
      }}
    >
      {collapsedRunning && <CollapsedTaskGlow />}
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isComplete ? '#22c55e' : '#A04F6A',
            flexShrink: 0,
          }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 13, color: '#2e3338', lineHeight: 1.2 }}>
              {progress.playbookName}
            </span>

          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            marginLeft: 'auto',
            marginRight: 12,
            fontSize: 10,
            fontWeight: 700,
            color: '#A04F6A',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {expanded ? 'Hide process' : 'Show process'}
        </button>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#5b5f65' }}>
          {completedCount} / {totalCount} steps
        </span>
      </div>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Progress bar */}
          <TaskProgressBar percent={progressPercent} active={isActive && !isComplete} failed={Object.values(progress.phaseStatuses).some(status => status === "failed")} />

          {/* Phase list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {progress.phases.map((phase) => {
              const status = progress.phaseStatuses[phase.id];
              const isCompleted = status === 'completed';
              const isRunning = status === 'running';
              return (
                <div key={phase.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  {isCompleted ? (
                    <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1 }}>&#x25CF;</span>
                  ) : isRunning ? (
                    <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1, opacity: 0.6 }}>&#x25D0;</span>
                  ) : (
                    <span style={{ color: '#aeb2b8', fontSize: 16, lineHeight: 1 }}>&#x25CB;</span>
                  )}
                  <span style={{
                    color: isCompleted ? '#2e3338' : '#5b5f65',
                    fontWeight: isRunning ? 600 : 400,
                    opacity: (!isCompleted && !isRunning) ? 0.6 : 1,
                  }}>
                    {playbookPhaseLabel(phase.name, status, progress.phaseStatusTexts[phase.id], isActive && !isComplete)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* QA Results */}
          {progress.qaResults.length > 0 && (
            <div style={{ fontSize: 12, color: '#5b5f65', fontWeight: 600 }}>
              QA: {progress.qaResults.filter((r) => r.passed).length}/{progress.qaResults.length} passed
            </div>
          )}

          {/* Concept progress */}
          {progress.conceptProgress && !isComplete && (
            <div style={{ fontSize: 12, color: '#A04F6A', fontWeight: 600 }}>
              Generating image {progress.conceptProgress.current} of {progress.conceptProgress.total}...
            </div>
          )}
        </div>
      )}

      {/* Intrinsic-ratio previews — inside white nested card */}
      {thumbnails.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 12,
          marginTop: 16,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          {thumbnails.map((img) => (
            <GeneratedImagePreview key={img.id} src={img.src} alt={img.adStyle ?? 'ad'}
              style={{borderRadius:8,background:'#eceef3'}}/>
          ))}
        </div>
      )}
    </div>
  );
}
